/*
  File: Navigation.tsx
  Author: Binish Mookken
  Date: April-June 2024

  Description:
  This file contains the Navigation component, which displays a vertical menu with various navigation options.
  It utilizes Ant Design's Menu component and Framer Motion for animations. Each menu item includes an icon, label,
  title (for tooltip), and tabindex for accessibility.
*/

import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu } from "antd";
import dashboardIcon from "./icons/dashboard.gif";
import agentsIcon from "./icons/agents.gif";
import myProfileIcon from "./icons/my-profile.gif";
import usersIcon from "./icons/users.gif";
import logoffIocn from "./icons/logoff.gif";
import logo from "./images/logo.png";
import { motion } from "framer-motion";
import "./Navigation.css";
import { useAuth } from "./context/AuthContext";
import { UserSwitchOutlined } from "@ant-design/icons";

interface NavigationProps {}

interface MenuItem {
  key: string;
  to: string;
  label: string;
  icon: string;
  title?: string;
  tabIndex?: number;
  onClick?: () => void;
}

const Navigation: React.FC<NavigationProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { logout, user, agentInstance } = useAuth();

  const hasPermission = (permissionName: string) => {
    // Check if any role has the specified permission
    return user?.roles.some((role) =>
      role.role_permissions.some(
        (permission) => permission.name === permissionName
      )
    );
  };

  useEffect(() => {
    navigate(menuItems[0].to);
  }, []);

  const menuItems: MenuItem[] = [
    hasPermission("ui_my_network") && {
      key: "/dashboard",
      to: "/dashboard",
      label: "My Network",
      icon: dashboardIcon,
      title: "Navigate to Dashboard to view the agent profile",
      tabIndex: 1,
    },
    hasPermission("ui_my_profile") && {
      key: "/my-profile",
      to: "/my-profile",
      label: "My Profile",
      icon: myProfileIcon,
      title: "Navigate to my profile to view login and access details",
      tabIndex: 3,
    },

    hasPermission("ui_agents") && {
      key: "/agents",
      to: "/agents",
      label: "Agents",
      icon: agentsIcon,
      title: "Navigate to view and manage agents",
      tabIndex: 2,
    },
    hasPermission("ui_users") && {
      key: "/settings",
      to: "/settings",
      label: "Users",
      icon: usersIcon,
      title: "Navigate to manage users",
      tabIndex: 4,
    },
    {
      key: "/",
      to: "/",
      label: "Logoff",
      icon: logoffIocn,
      title: "Navigate to logoff from the system",
      tabIndex: 5,
      onClick: () => {
        logout();
      },
    },
  ].filter(Boolean) as MenuItem[]; // Filter out null items

  const menuItemMotion = {
    whileHover: { x: [0, -5, 5, -5, 5, -5, 0] },
    whileTap: { scale: 0.9, rotate: -1 },
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLAnchorElement>,
    to: string
  ) => {
    if (e.key === "Enter") {
      window.location.href = to;
    }
  };

  return (
    <div className="navigation-container">
      <Menu
        theme="dark"
        mode="vertical"
        className="menu"
        selectedKeys={[location.pathname]}
      >
        <div className="logo-container">
          <img src={logo} alt="Logo" style={{ height: "100px" }} />
        </div>
        {menuItems.map((item) => (
          <Menu.Item
            key={item.key}
            title={item.title}
            tabIndex={item.tabIndex}
            onClick={item.onClick}
            style={{ maxWidth: "fit-content" }}
          >
            <motion.div {...menuItemMotion}>
              <Link
                key={item.key}
                to={item.to}
                className="menu-item"
                onKeyDown={(e) => handleKeyDown(e, item.to)}
              >
                <img className="header-icon" src={item.icon} alt={item.label} />
                <span className="menu-text">{item.label}</span>
              </Link>
            </motion.div>
          </Menu.Item>
        ))}
      </Menu>
    </div>
  );
};

export default Navigation;
